'use strict';
import React, { forwardRef, useMemo, useRef } from 'react';
import AppLink from './AppLink';
import Image from './Image';
import { runtime } from '../inc/Runtime';
import { splitFooterLabel } from '../inc/footerLabel';

const isExternal = (url = '') => {
	return /^https?:\/\//i.test(url) || url.startsWith('mailto:') || url.startsWith('tel:');
};

const normalizeLanguage = (value = '') => String(value || '').toLowerCase().trim().replace(/_/g, '-');

const currentFooterLanguage = (config = {}) => {
	const candidates = [
		config.language,
		runtime.route?.language,
		runtime.route?.lang,
		runtime.route?.locale,
		runtime.route?.data?.language,
		document.documentElement?.lang,
	].map(normalizeLanguage).filter(Boolean);
	const availableLanguages = Array.isArray(config.languages)
		? config.languages.map(language => normalizeLanguage(language?.code)).filter(Boolean)
		: [];

	if(!availableLanguages.length){
		return candidates[0] || '';
	}

	for(const candidate of candidates){
		if(availableLanguages.includes(candidate)){
			return candidate;
		}
	}

	return availableLanguages[0] || '';
};

const resolveNavigationLocation = (locations = {}, language = '', fallback = '') => {
	if(language && locations?.[language]){
		return locations[language];
	}

	return Object.values(locations || {}).find(Boolean) || fallback || '';
};

const resolveMenu = (navigation = {}, location = '') => {
	if(!location || !navigation || typeof navigation !== 'object'){
		return [];
	}

	const menu = navigation[location];

	return Array.isArray(menu) ? menu : [];
};

const styleVariables = (config = {}) => {
	const colors = config.colors || {};

	return Object.fromEntries(Object.entries({
		'--zero-un-footer-bg': colors.background,
		'--zero-un-footer-title': colors.title,
		'--zero-un-footer-text': colors.text,
		'--zero-un-footer-link': colors.link,
		'--zero-un-footer-muted': colors.muted,
		'--zero-un-footer-border': colors.border,
		'--zero-un-footer-link-hover': colors.linkHover,
		'--zero-un-footer-radius': config.borderRadiusValue,
		'--zero-un-footer-padding-top': config.paddingTop,
		'--zero-un-footer-blocks-gap': config.blocksGap,
		'--zero-un-footer-group-title-gap-desktop': config.groupTitleGap,
		'--zero-un-footer-group-title-gap-mobile': config.groupTitleMobileGap,
		'--zero-un-footer-logo-width': config.logoWidth,
		'--zero-un-footer-title-font-size-desktop': config.titleFontSize,
		'--zero-un-footer-title-font-size-mobile': config.titleMobileFontSize,
		'--zero-un-footer-text-font-size-desktop': config.itemFontSize,
		'--zero-un-footer-text-font-size-mobile': config.itemMobileFontSize,
		'--zero-un-footer-bottom-font-size-desktop': config.bottomFontSize,
		'--zero-un-footer-bottom-font-size-mobile': config.bottomMobileFontSize,
	}).filter(([, value]) => value));
};

const renderFooterLabel = (value = '') => {
	const label = String(value ?? '');
	const parts = splitFooterLabel(label);

	if(parts.length === 1){
		return label;
	}

	return parts.flatMap((part, index) => (
		index === 0
			? [part]
			: [React.createElement('br', { key: `br-${index}` }), part]
	));
};

const FooterLink = ({ item, className = '', children }) => {
	const url = item?.url || item?.href || '';
	const label = children || renderFooterLabel(item?.label || item?.title || url);

	if(!url){
		return <span className={className || undefined}>{label}</span>;
	}

	const target = item?.target || (isExternal(url) ? '_blank' : undefined);

	return (
		<AppLink
			className={className || undefined}
			to={url}
			target={target}
			rel={target === '_blank' ? 'noopener noreferrer' : undefined}
		>
			{label}
		</AppLink>
	);
};

const LinkList = ({ items = [], className = '' }) => {
	if(!items.length){
		return null;
	}

	return (
		<ul className={className || undefined}>
			{items.map((item, index) => (
				<li key={`${item?.url || item?.href || index}-${item?.label || item?.title || index}`}>
					<FooterLink item={item} />
				</li>
			))}
		</ul>
	);
};

const RichText = ({ html = '' }) => {
	if(!html){
		return null;
	}

	return <div className="zero-un-footer__rich-text" dangerouslySetInnerHTML={{ __html: html }} />;
};

const FooterGroup = ({ group, fallbackNavigationLocation, socialLinks }) => {
	const source = group?.source || 'custom_links';
	let body = null;

	if(source === 'navigation'){
		body = <LinkList items={resolveMenu(runtime.navigation, group?.navigationLocation || fallbackNavigationLocation)} />;
	}else if(source === 'social'){
		body = <LinkList items={socialLinks} />;
	}else if(source === 'text'){
		body = <RichText html={group?.content || ''} />;
	}else{
		body = <LinkList items={Array.isArray(group?.items) ? group.items : []} />;
	}

	if(!body){
		return null;
	}

	return (
		<section className={`zero-un-footer__group zero-un-footer__group--${source}`}>
			{group?.title && <h2>{renderFooterLabel(group.title)}</h2>}
			{body}
		</section>
	);
};

const FooterColumn = ({ column, fallbackNavigationLocation, socialLinks }) => {
	const groups = Array.isArray(column?.groups) ? column.groups : [];

	if(!groups.length){
		return null;
	}

	return (
		<div className="zero-un-footer__column">
			{groups.map((group, index) => (
				<FooterGroup
					key={`${group?.source || 'group'}-${group?.title || index}-${index}`}
					group={group}
					fallbackNavigationLocation={fallbackNavigationLocation}
					socialLinks={socialLinks}
				/>
			))}
		</div>
	);
};

const FooterLogo = ({ config }) => {
	if(!config.logo && config.variant === 'compact'){
		return null;
	}

	return (
		<AppLink className="zero-un-footer__brand" to="/" aria-label={runtime.site?.name || 'Accueil'}>
			{config.logo ? (
				<Image
					className="zero-un-footer__logo-media"
					data-media-key="footer-logo"
				/>
			) : (
				<span>{runtime.site?.name || 'LOGOTYPE'}</span>
			)}
		</AppLink>
	);
};

const Footer = forwardRef(function Footer({ show = false, className = null, ...props }, ref){

	const localRef = useRef(null);
	const footerRef = ref || localRef;
	const config = runtime.system?.zeroUn?.footer || {};
	const language = currentFooterLanguage(config);
	const navigationLocation = resolveNavigationLocation(config.navigationLocations, language, config.navigationLocation);
	const columns = useMemo(() => (
		Array.isArray(config.columns) ? config.columns.filter(column => Array.isArray(column?.groups) && column.groups.length) : []
	), [config.columns]);
	const socialLinks = Array.isArray(config.socialLinks) ? config.socialLinks : [];
	const legalLinks = Array.isArray(config.legalLinks) ? config.legalLinks : [];
	const variant = ['brand_left', 'brand_right', 'brand_center', 'compact'].includes(config.variant) ? config.variant : 'brand_left';
	const layout = config.layout === 'full' ? 'full' : 'boxed';
	const hasBorderRadius = Boolean(config.borderRadius);

	if(!show || config.enabled === false){
		return null;
	}

	const tagProps = {
		className: [
			'zero-un-footer',
			`zero-un-footer--${variant}`,
			`zero-un-footer--${layout}`,
			hasBorderRadius ? 'zero-un-footer--rounded' : '',
			className || '',
		].filter(Boolean).join(' '),
		style: styleVariables(config),
		...props
	}

	return(
		<footer ref={footerRef} {...tagProps}>
			<div className="zero-un-footer__shell">
				{variant === 'brand_center' ? (
					<>
						<div className="zero-un-footer__columns">
							{columns.map((column, index) => (
								<FooterColumn
									key={`column-${index}`}
									column={column}
									fallbackNavigationLocation={navigationLocation}
									socialLinks={socialLinks}
								/>
							))}
						</div>
						<div className="zero-un-footer__brand-wrap">
							<FooterLogo config={{ ...config, variant }} />
						</div>
					</>
				) : (
					<div className="zero-un-footer__main">
						{variant !== 'compact' && (
							<div className="zero-un-footer__brand-wrap">
								<FooterLogo config={{ ...config, variant }} />
							</div>
						)}
						<div className="zero-un-footer__columns">
							{columns.map((column, index) => (
								<FooterColumn
									key={`column-${index}`}
									column={column}
									fallbackNavigationLocation={navigationLocation}
									socialLinks={socialLinks}
								/>
							))}
						</div>
					</div>
				)}
				<div className="zero-un-footer__bottom">
					<p>{renderFooterLabel(config.copyright || `© ${new Date().getFullYear()} ${runtime.site?.name || ''}`)}</p>
					<div className="zero-un-footer__bottom-links">
						{legalLinks.map((item, index) => (
							<FooterLink item={item} key={`${item?.url || index}-${item?.label || index}`} />
						))}
						{config.credit?.label && config.credit?.url && (
							<FooterLink item={config.credit} />
						)}
					</div>
				</div>
			</div>
		</footer>
	);

});

export default Footer;
